import type {
  BrainstormClarifyingQuestionsData,
  BrainstormOptionsData,
  ClientStatus,
  CommentOptionSheet,
  ComplexityAgreement,
  MapLocationsData,
  OptionSheet,
  OptionSheetOption,
  SecureNote,
  TaskAttachment,
  TaskComment,
  TaskCommentAuthor,
  TaskCommentSingleChoiceOption,
  TaskDetail,
} from '@frontend/api-types';
import type { Currency } from '@frontend/duck-tape';

export const TASK_CHAT_FIXTURE_CONTENT_FORMAT = 'markdown' as const;

const TASK_CHAT_FIXTURE_ID_PREFIX = 'task-chat-fixture';

const createTaskChatFixtureId = (type: string, num: number = 1) =>
  `${TASK_CHAT_FIXTURE_ID_PREFIX}-${type}-${num.toString().padStart(3, '0')}`;

const createTaskDetail = (overrides: Partial<TaskDetail> = {}): TaskDetail =>
  ({
    aiQuestionnaire: null,
    attachments: [],
    clusterLabel: 'dining',
    createdAt: '2025-01-15T10:00:00.000Z',
    creditCost: 2,
    description:
      'Looking for a nice Italian restaurant in downtown for our anniversary dinner. We would like a romantic atmosphere, good wine selection, and vegetarian options available.',
    id: createTaskChatFixtureId('task'),
    isReviewSubmitted: false,
    isReviewed: false,
    isStarted: true,
    isUnread: false,
    keyInfo: null,
    maxExecutionHrs: 4,
    maxFollowupQuestions: 3,
    memberConfirmationStatus: undefined,
    memberConfirmedAt: undefined,
    name: 'Book restaurant reservation for anniversary dinner',
    ownerId: createTaskChatFixtureId('client'),
    proceedDecision: 'proceed',
    recurrence: undefined,
    recurrenceDate: undefined,
    searchThread: null,
    searchThreadId: null,
    secureNotes: [],
    sharedWith: [],
    sheets: [],
    shotClockStartedAt: '2025-01-15T10:30:00.000Z',
    startSlotFrom: null,
    startSlotTo: null,
    startedAt: '2025-01-15T10:30:00.000Z',
    status: 'open',
    statusSummary: 'In Progress',
    taskStatistics: null,
    taskSummary: null,
    template: {
      description: '',
      id: createTaskChatFixtureId('template'),
      image: '',
      logic: [],
      name: 'General Task',
      pages: [],
    },
    updatedAt: '2025-01-15T12:00:00.000Z',
    usedFollowupQuestions: 0,
    ...overrides,
  }) as TaskDetail;

export const fakeTask = createTaskDetail();

const createClientStatus = (overrides: Partial<ClientStatus> = {}): ClientStatus => ({
  currentActiveTasks: 1,
  freeTasksCap: null,
  hasPaymentMethod: true,
  individualMaxActiveTasks: 5,
  membershipStatus: 'active',
  onboardingComplete: true,
  onboardingStatus: 'card_added',
  planMaxActiveTasks: 10,
  promotionalFeedActive: false,
  promotionalFeedDurationInTasks: null,
  promotionalPartnerName: null,
  superAgentEnrollmentStatus: 'not_enrolled',
  tasksRemaining: 8,
  ...overrides,
});

export const fakeClientStatus = createClientStatus();

const createAuthor = (
  type: TaskCommentAuthor['type'],
  overrides: Partial<TaskCommentAuthor> = {},
): TaskCommentAuthor => ({
  firstName: type === 'client' ? 'You' : type === 'agent' ? 'Sarah' : undefined,
  isUser: type === 'client',
  lastName: type === 'agent' ? 'Johnson' : undefined,
  nickname: type === 'ai_bot' ? 'Duckbot' : type === 'ai_agent' ? 'Claude' : undefined,
  type,
  zendeskId: createTaskChatFixtureId(`zendesk-${type}`),
  ...overrides,
});

const createComment = (
  commentNum: number,
  author: TaskCommentAuthor,
  body: string,
  createdAt: string,
  overrides: Partial<TaskComment> = {},
): TaskComment =>
  ({
    attachments: [],
    author,
    body,
    createdAt,
    id: createTaskChatFixtureId('comment', commentNum),
    isUser: author.isUser,
    secureNote: null,
    sheets: [],
    singleChoiceOptions: [],
    ...overrides,
  }) as TaskComment;

const fakeImageAttachment: TaskAttachment = {
  contentType: 'image/jpeg',
  contentUrl: 'https://picsum.photos/400/300',
  fileName: 'restaurant_photo.jpg',
  id: createTaskChatFixtureId('attachment'),
};

const fakeSecureNote: SecureNote = {
  client: createTaskChatFixtureId('client'),
  createdAt: '2025-01-15T11:30:00.000Z',
  createdInTask: fakeTask.id,
  id: createTaskChatFixtureId('secure-note'),
  isActive: true,
  isBbContextReadyForService: true,
  isWebDomainSupportedOnMobile: true,
  title: 'OpenTable Login',
  updatedAt: '2025-01-15T11:30:00.000Z',
  webDomain: 'opentable.com',
} as SecureNote;

const acceptedGameplan: ComplexityAgreement = {
  agreementEndedAt: null,
  agreementStartedAt: '2025-01-15T11:00:00.000Z',
  complexityBucket: 'medium',
  creditCost: 3,
  id: createTaskChatFixtureId('gameplan'),
  status: 'accepted',
  title: 'Restaurant reservation confirmed',
} as ComplexityAgreement;

const selectedSingleChoiceOptions: TaskCommentSingleChoiceOption[] = [
  {
    acceptedAt: '2025-01-15T12:30:00.000Z',
    dismissedAt: null,
    id: createTaskChatFixtureId('choice'),
    optionType: 'yes_no',
    options: [
      { label: 'Yes, that works', value: 'yes' },
      { label: 'No, try another option', value: 'no' },
    ],
    selectedOption: 'yes',
  },
] as TaskCommentSingleChoiceOption[];

const OPTION_SHEET_ID = createTaskChatFixtureId('option-sheet');
const OPTION_GROUP_ID = createTaskChatFixtureId('option-group');

const fakeOptionSheetOptions: OptionSheetOption[] = [
  {
    createdAt: '2025-01-15T11:25:00.000Z',
    createdBy: createTaskChatFixtureId('zendesk-ai_agent'),
    description:
      "<p>Contemporary luxury in Guadalajara's most prestigious neighborhood with stunning rooftop infinity pool and panoramic city views.</p><ul><li>$380/night</li><li>4.6/5 stars</li><li>Zona Minerva location</li><li>Rooftop pool &amp; spa</li><li>3 restaurants</li><li>Executive lounge</li></ul>" as HTMLString,
    group: OPTION_GROUP_ID,
    id: createTaskChatFixtureId('option', 1),
    imageUrls: ['https://picsum.photos/seed/hotel-fiesta/600/400'],
    isVisible: true,
    order: 1,
    price: 38000 as Cent,
    priceDisplay: '$380/night',
    title: 'Grand Fiesta Americana Country Club',
    updatedAt: '2025-01-15T11:25:00.000Z',
    updatedBy: null,
    url: 'https://example.com/grand-fiesta',
  },
  {
    createdAt: '2025-01-15T11:25:00.000Z',
    createdBy: createTaskChatFixtureId('zendesk-ai_agent'),
    description:
      '<p>Ultra-exclusive boutique hotel with only 20 suites, award-winning restaurant, and curated art collection throughout. Perfect for intimate, culturally-rich stays.</p><ul><li>$420/night</li><li>4.8/5 stars (highest rated)</li><li>Chapalita location</li><li>Only 20 suites</li><li>Gourmet dining (Condimento)</li><li>Personal concierge</li></ul>' as HTMLString,
    group: OPTION_GROUP_ID,
    id: createTaskChatFixtureId('option', 2),
    imageUrls: ['https://picsum.photos/seed/hotel-demetria/600/400'],
    isVisible: true,
    order: 2,
    price: 42000 as Cent,
    priceDisplay: '$420/night',
    title: 'Hotel Demetria',
    updatedAt: '2025-01-15T11:25:00.000Z',
    updatedBy: null,
    url: 'https://example.com/hotel-demetria',
  },
  {
    createdAt: '2025-01-15T11:25:00.000Z',
    createdBy: createTaskChatFixtureId('zendesk-ai_agent'),
    description:
      '<p>Elegant 1930s mansion turned luxury hotel with old-world charm, garden courtyard, and wine cellar. Walking distance to galleries and restaurants.</p><ul><li>$365/night</li><li>4.7/5 stars</li><li>Lafayette/Historic district</li><li>Historic mansion</li><li>Gourmet breakfast included</li><li>Wine cellar</li></ul>' as HTMLString,
    group: OPTION_GROUP_ID,
    id: createTaskChatFixtureId('option', 3),
    imageUrls: ['https://picsum.photos/seed/hotel-villa/600/400'],
    isVisible: true,
    order: 3,
    price: 36500 as Cent,
    priceDisplay: '$365/night',
    title: 'Villa Ganz Hotel Boutique',
    updatedAt: '2025-01-15T11:25:00.000Z',
    updatedBy: null,
    url: 'https://example.com/villa-ganz',
  },
  {
    createdAt: '2025-01-15T11:25:00.000Z',
    createdBy: createTaskChatFixtureId('zendesk-ai_agent'),
    description:
      '<p>Contemporary design with reliable luxury standards. Features two pools including rooftop, full spa, and excellent location in the entertainment district.</p><ul><li>$355/night (best value)</li><li>4.5/5 stars</li><li>Midtown/Zona Rosa</li><li>2 pools (rooftop pool)</li><li>Full spa &amp; wellness</li><li>Multiple dining options</li></ul>' as HTMLString,
    group: OPTION_GROUP_ID,
    id: createTaskChatFixtureId('option', 4),
    imageUrls: ['https://picsum.photos/seed/hotel-hilton/600/400'],
    isVisible: true,
    order: 4,
    price: 35500 as Cent,
    priceDisplay: '$355/night',
    title: 'Hilton Guadalajara Midtown',
    updatedAt: '2025-01-15T11:25:00.000Z',
    updatedBy: null,
    url: 'https://example.com/hilton-gdl',
  },
  {
    createdAt: '2025-01-15T11:25:00.000Z',
    createdBy: createTaskChatFixtureId('zendesk-ai_agent'),
    description:
      '<p>Modern high-rise hotel with panoramic city views, expansive pool deck, and convenient access to business and cultural attractions.</p><ul><li>$290/night</li><li>4.4/5 stars</li><li>Downtown/Centro</li><li>Rooftop pool &amp; bar</li><li>Fitness center</li><li>Business center</li></ul>' as HTMLString,
    group: OPTION_GROUP_ID,
    id: createTaskChatFixtureId('option', 5),
    imageUrls: ['https://picsum.photos/seed/hotel-hyatt/600/400'],
    isVisible: true,
    order: 5,
    price: 29000 as Cent,
    priceDisplay: '$290/night',
    title: 'Hyatt Regency Guadalajara',
    updatedAt: '2025-01-15T11:25:00.000Z',
    updatedBy: null,
    url: 'https://example.com/hyatt-gdl',
  },
  {
    createdAt: '2025-01-15T11:25:00.000Z',
    createdBy: createTaskChatFixtureId('zendesk-ai_agent'),
    description:
      '<p>Charming colonial-era hotel with traditional Mexican architecture, lush garden courtyard, and complimentary mezcal tasting. Steps from Tlaquepaque artisan market.</p><ul><li>$195/night</li><li>4.3/5 stars</li><li>Tlaquepaque</li><li>Garden courtyard</li><li>Mezcal bar</li><li>Walking tours</li></ul>' as HTMLString,
    group: OPTION_GROUP_ID,
    id: createTaskChatFixtureId('option', 6),
    imageUrls: ['https://picsum.photos/seed/hotel-colonial/600/400'],
    isVisible: true,
    order: 6,
    price: 19500 as Cent,
    priceDisplay: '$195/night',
    title: 'Casa Colonial Tlaquepaque',
    updatedAt: '2025-01-15T11:25:00.000Z',
    updatedBy: null,
    url: '',
  },
] as OptionSheetOption[];

export const fakeOptionSheet: OptionSheet = {
  createdAt: '2025-01-15T11:25:00.000Z',
  createdBy: createTaskChatFixtureId('zendesk-ai_agent'),
  currency: 'USD' as Currency,
  groups: [
    {
      id: OPTION_GROUP_ID,
      isVisible: true,
      options: fakeOptionSheetOptions,
    },
  ],
  id: OPTION_SHEET_ID,
  isVisible: true,
  publicUrl: '',
  publishedAt: '2025-01-15T11:25:00.000Z',
  reviewFeedback: '',
  reviewedAt: '',
  selectedOptionIds: [],
  task: createTaskChatFixtureId('task'),
  title: 'Top 5 Luxury Hotels in Guadalajara',
  updatedAt: '2025-01-15T11:25:00.000Z',
  updatedBy: createTaskChatFixtureId('zendesk-ai_agent'),
} as OptionSheet;

const fakeCommentOptionSheet: CommentOptionSheet = {
  id: OPTION_SHEET_ID,
  imageUrl: 'https://picsum.photos/seed/hotel-fiesta/600/400',
  isReviewed: false,
  isViewable: true,
  title: 'Top 5 Luxury Hotels in Guadalajara',
};

export const fakeClarifyingQuestions: BrainstormClarifyingQuestionsData = {
  questions: [
    {
      header: 'Hotel Style',
      multiSelect: false,
      options: [
        { description: 'Modern high-rise with full amenities and services', label: 'Large luxury hotel' },
        { description: 'Intimate property with unique character and personalized service', label: 'Boutique hotel' },
        { description: 'Fully furnished apartment or house for a home-away-from-home feel', label: 'Vacation rental' },
      ],
      question: 'What type of accommodation do you prefer?',
    },
    {
      header: 'Priorities',
      multiSelect: true,
      options: [
        { description: 'Pool, spa, fitness center, and other wellness amenities', label: 'Pool & spa' },
        { description: 'On-site restaurants, bars, and room service', label: 'Dining options' },
        { description: 'Close to attractions, restaurants, and nightlife', label: 'Central location' },
        { description: 'Quiet area with beautiful views and outdoor space', label: 'Scenic & peaceful' },
      ],
      question: 'Which amenities matter most to you?',
    },
    {
      header: 'Budget',
      multiSelect: false,
      options: [
        { description: 'Up to $200 per night', label: 'Budget-friendly' },
        { description: '$200-400 per night', label: 'Mid-range' },
        { description: '$400+ per night', label: 'Luxury' },
      ],
      question: 'What is your nightly budget range?',
    },
  ],
  type: 'clarifying_questions',
};

export const fakeBrainstormOptions: BrainstormOptionsData = {
  followupQuestion:
    'Would you like me to check availability for any of these options, or would you like to adjust the criteria?',
  options: [
    {
      attributes: {
        Location: 'Zona Minerva',
        'Nightly Rate': '$380',
        Rating: '4.6/5 stars',
        Style: 'Contemporary luxury',
      },
      description:
        "Contemporary luxury in Guadalajara's most prestigious neighborhood with stunning rooftop infinity pool and panoramic city views. Features 3 restaurants, executive lounge, and full spa.",
      imageUrl: 'https://picsum.photos/seed/hotel-fiesta/600/400',
      price: '$380/night',
      title: 'Grand Fiesta Americana Country Club',
      url: 'https://example.com/grand-fiesta',
    },
    {
      attributes: {
        Location: 'Chapalita',
        'Nightly Rate': '$420',
        Rating: '4.8/5 stars (highest rated)',
        Style: 'Ultra-exclusive boutique',
      },
      description:
        'Ultra-exclusive boutique hotel with only 20 suites, award-winning restaurant Condimento, and curated art collection. Perfect for intimate, culturally-rich stays with personal concierge.',
      imageUrl: 'https://picsum.photos/seed/hotel-demetria/600/400',
      price: '$420/night',
      title: 'Hotel Demetria',
      url: 'https://example.com/hotel-demetria',
    },
    {
      attributes: {
        Location: 'Lafayette/Historic district',
        'Nightly Rate': '$365',
        Rating: '4.7/5 stars',
        Style: 'Historic mansion',
      },
      description:
        'Elegant 1930s mansion turned luxury hotel with old-world charm, garden courtyard, wine cellar, and gourmet breakfast included. Walking distance to galleries and restaurants.',
      imageUrl: 'https://picsum.photos/seed/hotel-villa/600/400',
      price: '$365/night',
      title: 'Villa Ganz Hotel Boutique',
      url: 'https://example.com/villa-ganz',
    },
  ],
  phase: 'initial',
  summary:
    'Based on your preferences for a boutique hotel with pool & spa and dining options in the mid-range budget, here are my top 3 picks for your Guadalajara trip:',
  type: 'brainstorm_options',
};

export const fakeMapLocations: MapLocationsData = {
  locations: [
    {
      label: 'Hotel Hugo (Home Base)',
      lat: 40.7259,
      lng: -74.0098,
      placeId: 'ChIJ2Xb_adxZwokR_uYe6mwIaZQ',
    },
    {
      label: 'Ground Support Cafe',
      lat: 40.7243,
      lng: -74.002,
      placeId: 'ChIJIXmQFYxZwokRhxGPUQv0TRk',
    },
    {
      label: 'La Colombe Coffee',
      lat: 40.7237,
      lng: -73.9978,
      placeId: 'ChIJ8wF3FI9ZwokRrz0RxZ6u9HY',
    },
    {
      label: 'Laughing Man Coffee',
      lat: 40.7173,
      lng: -74.0101,
      placeId: 'ChIJh_u0pR9awokRn78h97cEAV4',
    },
    {
      label: 'Stumptown Coffee Roasters',
      lat: 40.7328,
      lng: -73.9979,
      placeId: 'ChIJ7c9s1JZZwokRs_zFHYCtNDA',
    },
  ],
  message: 'Here are some nearby coffee shops I found for you:',
  type: 'map_locations',
};

export const fakeComments: TaskComment[] = [
  createComment(
    1,
    createAuthor('client'),
    "Hi! I need help booking a restaurant for our anniversary dinner this Saturday. Looking for somewhere romantic with good Italian food in downtown. My partner is vegetarian, so we'd need good veggie options.",
    '2025-01-15T10:00:00.000Z',
  ),
  createComment(2, createAuthor('system_bot'), 'Your task has been received.', '2025-01-15T10:01:00.000Z'),
  createComment(3, createAuthor('system_bot'), 'Sarah Johnson was assigned to your task.', '2025-01-15T10:01:30.000Z'),
  createComment(
    4,
    createAuthor('ai_bot'),
    "Hi there! I'm getting your task set up. Here's what I noted:\n\n- **Occasion:** Anniversary dinner\n- **Date:** This Saturday\n- **Cuisine:** Italian\n- **Requirements:** Vegetarian options, romantic atmosphere\n\nA Copilot will be with you shortly!",
    '2025-01-15T10:02:00.000Z',
  ),
  createComment(
    5,
    createAuthor('agent'),
    "Hello! I'm Sarah, and I'll be helping with your anniversary dinner. Congratulations! 🎉\n\nI have a few quick questions:\n\n1. What time works best for you?\n2. Any budget range in mind?\n3. Do you prefer a quieter spot or lively atmosphere?",
    '2025-01-15T10:10:00.000Z',
  ),
  createComment(
    6,
    createAuthor('client'),
    "Thanks Sarah! We're thinking around 7-8 PM. Budget is flexible, maybe $150-200 for the two of us? Definitely quieter - we want to be able to talk without shouting!",
    '2025-01-15T10:15:00.000Z',
  ),
  createComment(
    7,
    createAuthor('agent'),
    "Perfect! I've put together a plan for your special evening:\n\n**What I'll do:**\n\n1. Research top-rated Italian restaurants downtown\n2. Verify vegetarian menus and romantic ambiance\n3. Check Saturday evening availability\n4. Compare pricing and reviews\n5. Make the reservation once you approve\n\n**Timeline:** I should have options for you within the hour.",
    '2025-01-15T10:20:00.000Z',
    { complexityAgreement: acceptedGameplan },
  ),
  createComment(8, createAuthor('system_bot'), 'Task status updated to *In Progress*.', '2025-01-15T10:25:00.000Z'),
  createComment(
    9,
    createAuthor('agent'),
    "Quick update - I found some great options! One of my top picks (Osteria Romano) has limited availability, so I'd like to check their reservation system directly.\n\nI've created a secure note for your **OpenTable login** if you'd like me to book through your account. This keeps your rewards points intact!",
    '2025-01-15T11:00:00.000Z',
    { secureNote: fakeSecureNote },
  ),
  createComment(
    10,
    createAuthor('client'),
    "Sure, I've added my login. Also, here's a photo from a restaurant we loved in Italy - something with this vibe would be amazing!",
    '2025-01-15T11:10:00.000Z',
    { attachments: [fakeImageAttachment] },
  ),
  createComment(
    11,
    createAuthor('system_bot'),
    `Copilot accessed [OpenTable Login](/secure-note/${fakeSecureNote.id}).`,
    '2025-01-15T11:15:00.000Z',
  ),
  createComment(
    12,
    createAuthor('ai_agent'),
    'Before I start searching, let me ask a few quick questions to narrow down the best options for you:',
    '2025-01-15T11:20:00.000Z',
    { questions: fakeClarifyingQuestions },
  ),
  createComment(
    13,
    createAuthor('client'),
    'Boutique hotel, Pool & spa + Dining options, Mid-range',
    '2025-01-15T11:22:00.000Z',
  ),
  createComment(14, createAuthor('ai_agent'), '', '2025-01-15T11:25:00.000Z', { questions: fakeBrainstormOptions }),
  createComment(
    15,
    createAuthor('ai_agent'),
    'Here are the **top 5 luxury hotels** in Guadalajara for March 15-18:',
    '2025-01-15T11:30:00.000Z',
    { sheets: [fakeCommentOptionSheet] },
  ),
  createComment(
    16,
    createAuthor('client'),
    "Wow, these all look great! Let's go with Osteria Romano - the 7:30 slot works perfectly. The vegetarian tasting menu sounds amazing for my partner.",
    '2025-01-15T11:35:00.000Z',
  ),
  createComment(
    17,
    createAuthor('system_bot'),
    'Confirm reservation at Osteria Romano for Saturday 7:30 PM?',
    '2025-01-15T11:40:00.000Z',
    { singleChoiceOptions: selectedSingleChoiceOptions },
  ),
  createComment(
    18,
    createAuthor('agent'),
    "Your reservation is confirmed! 🎉\n\n**Reservation Details:**\n\n- **Restaurant:** Osteria Romano\n- **Date:** Saturday, January 18th\n- **Time:** 7:30 PM\n- **Party size:** 2 guests\n- **Confirmation #:** `OR-2025-0118-7890`\n\n**Special notes added:**\n- Anniversary celebration\n- One vegetarian tasting menu requested\n- Quiet table preferred\n\n**Address:** 425 Italian Way, Downtown\n\nI've also added this to your calendar!",
    '2025-01-15T12:00:00.000Z',
  ),
  createComment(19, createAuthor('system_bot'), 'Reservation confirmed via OpenTable.', '2025-01-15T12:01:00.000Z'),
  createComment(
    20,
    createAuthor('system_bot'),
    'Calendar event created for Saturday 7:30 PM.',
    '2025-01-15T12:01:15.000Z',
  ),
  createComment(21, createAuthor('system_bot'), 'Confirmation email sent to your inbox.', '2025-01-15T12:01:30.000Z'),
  createComment(
    22,
    createAuthor('client'),
    "This is perfect, thank you so much Sarah! You've made planning this so easy. Can't wait for Saturday!",
    '2025-01-15T12:10:00.000Z',
  ),
  createComment(
    23,
    createAuthor('agent'),
    "You're so welcome! Have a wonderful anniversary! 💕\n\n**Pro tips for Osteria Romano:**\n\n- Ask for the *Brunello di Montalcino* - it pairs beautifully with their dishes\n- Save room for the tiramisu (made fresh daily)\n- Valet parking is available on weekends\n\nLet me know if you need anything else before Saturday!",
    '2025-01-15T12:15:00.000Z',
  ),
  createComment(24, createAuthor('system_bot'), 'Task marked as complete.', '2025-01-15T12:20:00.000Z'),
  createComment(
    25,
    createAuthor('system_bot'),
    '3 credits used. Remaining balance: 6 credits.',
    '2025-01-15T12:20:05.000Z',
  ),
  createComment(
    26,
    createAuthor('system_bot'),
    'How did we do? [Share your feedback](/settings/feedback).',
    '2025-01-15T12:20:10.000Z',
  ),
  createComment(
    27,
    createAuthor('system_bot'),
    'Need to modify your reservation? Contact [Osteria Romano](https://www.osteriaromano.com) or email [reservations@osteriaromano.com](mailto:reservations@osteriaromano.com).',
    '2025-01-15T12:25:00.000Z',
  ),
  createComment(28, createAuthor('ai_agent'), fakeMapLocations.message, '2025-01-15T12:30:00.000Z', {
    mapLocations: fakeMapLocations,
  }),
];
